import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'

import { SurveyPageType } from '@/enums/pageTypes'
import { debounce } from '@/utils/functionalUtils'
import { getPageName } from '@/utils/urlUtils'

import { Option } from '@/components/option'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextHTML } from '@/components/typography'
import { RatingDescriptionArrow } from '@/shared/icons/RatingDescriptionArrow'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageComplete } from '@/events/events'

import { TOneSelectPageProps } from './types'

export function OneSelectPage({
  pageName,
  parameter,
  pageConfig,
  setItem,
  setLastVisitedItem,
  config,
  pageType = SurveyPageType.DEFAULT,
}: TOneSelectPageProps) {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [selectedOptions, setSelectedOptions] = useState<Array<number>>([])

  const {
    title,
    options = [],
    nextUrl,
    infoImage,
    backgroundImage,
    decorativeImageOptions,
    pageDescription,
    tabTitle,
    replaceParameter,
    policyBlock,
    ratingDescription,
    hideIndicator,
    showDefaultCircle,
    customCheckbox,
  } = pageConfig

  const navigate = useNavigate()

  const description =
    parameter && pageDescription && replaceParameter
      ? replaceParameter(parameter, pageDescription)
      : pageDescription

  const pageTitle = typeof title === 'function' ? title() : title
  usePageLoadTracking({
    pageName,
    config,
    user,
    title: tabTitle || pageTitle,
  })

  const getSelectedTitles = useCallback((): string[] => {
    const selectedTitles: string[] = []
    for (const index of selectedOptions) {
      const title = options[index]?.value || options[index]?.title
      if (title) {
        selectedTitles.push(title)
      }
    }
    return selectedTitles
  }, [selectedOptions, options])

  const goToNextPage = debounce(async () => {
    const selectedTitles = getSelectedTitles()
    trackPageComplete(
      pageName,
      selectedTitles,
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )
    const optionIndex = selectedOptions[0]

    const currentOption = options && options[optionIndex]
    const optionNextUrl = currentOption && currentOption?.nextUrl

    navigate(optionNextUrl ? optionNextUrl : nextUrl)
    if (optionNextUrl) {
      setLastVisitedItem(getPageName(optionNextUrl))
    } else {
      setLastVisitedItem(pageName)
    }
  }, 200)

  const isRatingPage = pageType === SurveyPageType.RATING

  useEffect(() => {
    if (selectedOptions.length) {
      goToNextPage()
    }
  }, [selectedOptions, goToNextPage, pageName, nextUrl])

  const selectOption = useCallback(
    (currentOptionIndex: number) => {
      setItem(pageName, options[currentOptionIndex].storageValue || currentOptionIndex.toString())
      if (!selectedOptions.length) {
        setSelectedOptions([currentOptionIndex])
      }
    },
    [pageName, setItem, selectedOptions, options]
  )

  const className = `page page-survey page-${pageName}`
  const hasBackgroundImage = Boolean(backgroundImage)

  let optionContainerClassName = 'survey-options-container'
  if (hasBackgroundImage) {
    optionContainerClassName += ' with-image'
  }

  if (isRatingPage) {
    optionContainerClassName += ' rating-container'
  }
  return (
    <div className={className}>
      {infoImage && (
        <div className="info-image-container">
          <img src={infoImage.src} alt="" />
        </div>
      )}

      <PageTitle title={pageTitle} />

      {description && <TextHTML className="page-description" title={description} />}

      <div className={optionContainerClassName}>
        {options?.map((option, index) => (
          <Option
            key={`${option.title}${index}`}
            index={index}
            title={option.title}
            description={option.description}
            image={option.image}
            emoji={option.emoji}
            decorativeImage={decorativeImageOptions}
            selectOption={selectOption}
            selectedOptions={selectedOptions}
            isOneSelectPage
            hideIndicator={hideIndicator}
            showDefaultCircle={showDefaultCircle}
            customCheckbox={customCheckbox}
          />
        ))}

        {hasBackgroundImage && (
          <div className="image-container">
            {backgroundImage && typeof backgroundImage === 'object' && 'src' in backgroundImage ? (
              <img src={backgroundImage.src} alt="" loading="lazy" />
            ) : (
              backgroundImage
            )}
          </div>
        )}
      </div>
      {isRatingPage && (
        <div className="rating-description-container">
          {ratingDescription?.showArrow && (
            <div className="rating-arrow">
              <RatingDescriptionArrow />
            </div>
          )}
          <div>{ratingDescription?.leftPart}</div>
          <div>{ratingDescription?.rightPart}</div>
        </div>
      )}
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
